import {nanoid} from 'nanoid';

export function makeReactiveProperty<T, P extends keyof T, O>(that: T, target: O, prop: P, name: string): O {
  Object.defineProperty(target, name, {
    enumerable: true,
    get: () => that[prop],
  });
  return target;
}

export function generateUID(size = 10): string {
  return nanoid(size);
}

export function compareStrings(first: string, second: string): number {
  return first.localeCompare(second);
}

export function compareNumbers(first: number, second: number): number {
  return first - second;
}

type SortIndex = number | string
type SortDirection = 'asc' | 'desc'

interface ISortable {
  origObject: object;
  sortIndex: SortIndex[];
}

type SortOptionResolver<Item> = (item: Item) => [SortIndex, SortDirection?][]

export function sortBy<Item>(data: Item[], optionResolver: SortOptionResolver<Item>): Item[] {
  if (!data || data.length < 2) return [ ...data ];

  const sortables = data.map((object) => {
    const sortIndex = optionResolver(object).map(args => args[0]);

    return {
      origObject: object,
      sortIndex: sortIndex,
    };
  });

  const indexLength = sortables[0].sortIndex.length;

  const directions: SortDirection[] = optionResolver(data[0])
    .map((args) => {
      const direction = args[1];

      if (typeof direction === 'undefined') {
        return 'asc';
      } else {
        return direction;
      }
    });

  sortables.sort((a, b) => {
    let valueIndex = 0;
    let compResult = 0;

    do {
      const [ first, second ] = [ a.sortIndex[valueIndex], b.sortIndex[valueIndex] ];

      const invert = directions[valueIndex] === 'desc';

      if (String(first) !== String(second)) {
        if (typeof first === 'string' && typeof second === 'string') {
          compResult = compareStrings(first, second);
        } else if (typeof first === 'number' && typeof second === 'number') {
          compResult = compareNumbers(first, second);
        } else {
          compResult = compareStrings(String(first), String(second));
        }
      } else {
        valueIndex++;
      }

      if (invert) {
        compResult = compResult * -1;
      }

      if (compResult !== 0) break;
    } while (valueIndex <= indexLength);
    return compResult;
  });

  return sortables.map((sortable) => sortable.origObject);
}

export type WithUID<T> = T & {
  $_UID: string;
};

export type WithDestroy<T> = T & {
  _destroy: boolean | undefined;
};

export type KeysArray<T> = Array<keyof T>;

export function toPascalCase(str: string): string {
  return str.toLowerCase().replace(/(?:(^.)|(\s+.))/g, match => {
    return match.charAt(match.length - 1).toUpperCase();
  });
}

// Creates a popup window centered on the window whose reference you pass to it
export function createCenteredPopupWindow(
  window: Window,
  url: string,
  name: string,
  desiredHeight: number,
  desiredWidth: number,
  options: string | null = null,
) {
  const y = window.top.outerHeight / 2 + window.top.screenY - ( desiredHeight / 2 );
  const x = window.top.outerWidth / 2 + window.top.screenX - ( desiredWidth / 2 );
  const sizeOptions = `width=${desiredWidth},height=${desiredHeight},top=${y},left=${x}`;
  let finalOptions;
  if (options) {
    finalOptions = sizeOptions + ',' + options;
  } else {
    finalOptions = sizeOptions;
  }
  return window.open(url, name, finalOptions);
}
